import { useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquarePlus, Loader2 } from "lucide-react";

// Reason Quick Fill: one reason for every selected parcel that has none yet.
export const BulkReasonDialog = ({ open, onClose, ids, suggestions, onDone }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const close = () => { setReason(""); onClose(); };

  const apply = async () => {
    if (!reason.trim()) { toast.error("Pick or type a reason"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/order-register/bulk-reason`,
        { ids, reason: reason.trim() }, { headers: getAuthHeader() });
      const { parcels, skipped } = r.data;
      toast.success(`Reason set on ${parcels} parcel${parcels === 1 ? "" : "s"}`
        + (skipped ? ` · ${skipped} skipped (already had a reason)` : ""));
      setReason("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set reason");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="or-bulk-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <MessageSquarePlus className="w-5 h-5" /> Set reason for {ids.length} selected row{ids.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Applied to the whole parcel. Parcels that already have a reason are skipped. Every change is logged in the audit trail.
          </DialogDescription>
        </DialogHeader>
        <Input autoFocus value={reason} list="or-bulk-reason-list" maxLength={500}
               placeholder="Pick or type a reason…"
               onChange={(e) => setReason(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
               data-testid="or-bulk-reason-input" />
        <datalist id="or-bulk-reason-list">
          {suggestions.map(s => <option key={s} value={s} />)}
        </datalist>
        <div className="flex flex-wrap gap-1.5" data-testid="or-bulk-reason-suggestions">
          {suggestions.map(s => (
            <button key={s} type="button" onClick={() => setReason(s)}
              data-testid={`or-bulk-reason-sugg-${s.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                reason === s ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50"}`}>
              {s}
            </button>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} data-testid="or-bulk-reason-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" onClick={apply} disabled={saving}
                  data-testid="or-bulk-reason-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquarePlus className="w-4 h-4 mr-1" />}
            Apply reason
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
